<template>
  <!-- Advertisement Details Drawer -->
  <BaseDrawer
    no-padding
    height-auto-only
    no-bottom-rounding
    noToolbar
    class="scrollbar-hide"
    :mobileView="mobileView"
    :is-child-drawer="isChildDrawer"
    @close="$emit('close'), SET_AD_PLAYING('')"
  >
    <template #content>
      <!-- Content -->
      <div data-tour="drawer-root" class="w-full flex rounded-t-3xl bg-neutral-50" :class="mobileView ? 'flex-col p-2' : 'flex-row overflow-hidden'"
      :style="{height: mobileView ? 'auto' : 'calc(100vh - 80px)', minHeight: mobileView ? 'calc(100vh - 80px)' : 'auto'}">
        <!-- TOOLBAR & AD CARD -->
        <div class="flex flex-col flex-grow" :style="{height: mobileView ? 'auto' : 'calc(100vh - 80px)'}">
          <!-- TOOLBAR -->
          <div class="toolbar relative z-30 w-full flex items-center justify-between mt-1" :class="{'px-5 py-4': !mobileView}">
            <!-- Previous ad button -->
            <button v-if="!mobileView" class="cycle-btn flex items-center gap-1.5 pl-1.5 py-1.5 pr-3 rounded-md"
            :class="previousAd ? 'transition-colors hover:bg-neutral-25' : 'cursor-default opacity-80'"
            :disabled="!previousAd" @click="previousAdvertisement">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none" class="transition-colors"
              :class="previousAd ? 'text-icon-normal' : 'text-icon-disabled'">
                <path d="M11.3215 13.3327L8.57742 10.5886C8.25198 10.2632 8.25198 9.73553 8.57742 9.41009L11.3215 6.66602"
                stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              <BaseText type="label" size="sm" class="transition-colors" :class="previousAd ? 'text-text-muted' : 'text-text-disabled'">
                Previous
              </BaseText>
            </button>
            <!-- Center toolbar actions -->
            <div class="actions-shadow flex items-center gap-1 p-2 bg-white min-w-0 flex-shrink " 
            style="border-radius: 14px" :class="mobileView ? 'mx-auto w-full' : 'center-toolbar-actions mx-0 w-auto'">
              <!-- External ad link -->
              <div v-if="currentAd.ad_id" class="flex items-center gap-1 px-1.5 rounded-md min-w-0 flex-shrink">
                <BaseText type="body" size="sm" class="text-text-normal">
                  ID:
                </BaseText>
                <BaseText type="label" size="sm" class="text-text-muted min-w-0 flex-shrink truncate">
                  {{ currentAd.ad_id }}
                </BaseText>
                <a v-if="currentAd.live && currentAd?.publisher_platform.some((platform) => ['facebook', 'instagram'].includes(platform)) && currentAd.ad_id"
                :href="currentAd.organic ? getInstagramUrl : `https://www.facebook.com/ads/library/?id=${currentAd.ad_id}`"
                target="_blank" class="group p-1.5 rounded-md transition-colors hover:bg-neutral-25">
                  <OpenExternalIcon class="text-icon-normal transition-colors group-hover:text-icon-muted" />
                </a>
              </div>

              <!-- Vertical line spacer -->
              <div v-if="currentAd.ad_id" class="w-px h-4 bg-neutral-50" :class="mobileView ? 'mx-auto' : 'mx-1'" />

              <!-- Same creative count -->
              <div @click="showSameCreativeTooltip = !showSameCreativeTooltip" class="flex relative z-30 cursor-pointer select-none items-center gap-1 px-1.5 rounded-md min-w-0 flex-shrink hover:bg-neutral-25">
                <SameCreativeIcon class="text-icon-muted" />
                <div v-if="currentAd.ad_id" class="flex items-center gap-1 px-1.5 py-1.5 rounded-md min-w-0 flex-shrink">
                  <BaseText type="label" size="sm" class="text-text-muted min-w-0 flex-shrink truncate">
                    {{ Math.max(1, currentAd.sameCreativeCount || currentAd?.cards?.[carouselIndex]?.sameCreativeCount || 1) }}
                  </BaseText>
                  <ChevronIcon class="text-icon-muted transition-transform duration-200" :class="{ 'rotate-180': showSameCreativeTooltip }" />
                </div>

                <!-- Same creative tooltip -->
                <transition>
                  <div v-if="showSameCreativeTooltip" class="absolute z-50 text-left flex flex-col p-1 bg-neutral-700 shadow-lg overflow-y-scroll scrollbar-hide" style="top: calc(100% + 6px); left: -2px; width: 240px; border-radius: 10px; max-height: 300px;">
                    <div class="flex gap-1 items-start py-1.5 px-1">
                      <SameCreativeIcon class="text-white" style="opacity: 0.76;" />
                      <BaseText type="body" size="sm" class="text-white font-normal" style="opacity: 0.84;">
                        Creative Duplicates
                      </BaseText>
                    </div>
                    <div
                      v-for="ad in relatedAdsList"
                      :key="ad.id"
                      @click="loadAdvertisement(ad.id)"
                      :class="[
                        'flex items-center gap-1 rounded-md p-1 my-px same-creative-link',
                        { 'pointer-events-none cursor-default': isCurrentRelatedAd(ad) }
                      ]"
                      :style="{ background: isCurrentRelatedAd(ad) ? 'rgba(255, 255, 255, 0.04)' : 'rgba(255, 255, 255, 0.08)' }"
                    >
                      <!-- Live Indicator -->
                      <div class="flex items-center gap-1 px-1 py-0.5" style="border-radius: 3px;" :style="{ backgroundColor: ad.live ? '#184E44' : 'rgba(255, 255, 255, 0.05)' }">
                          <div class="h-1.5 w-1.5 rounded-full" :style="{ backgroundColor: ad.live ? '#40C4AA' : '#DFE1E7' }"/>
                          <BaseText type="label" size="xs" class="font-normal truncate" :class="{
                            'text-white': !ad.live,
                            'text-secondary-green-100': ad.live
                          }" :style="{ opacity: ad.live ? 1 :  0.76 }">
                            {{ formatRunningDuration(ad.started_running, ad.live ? new Date().getTime() : getAdEffectiveEndDate(ad)) }}D
                          </BaseText>
                      </div>
                      <BaseText type="body" size="sm" class="text-white font-normal truncate" style="opacity: 0.76;">
                        ID
                      </BaseText>
                      <BaseText type="body" size="sm" class="text-white font-normal truncate" style="opacity: 0.68;">
                        {{ ad.ad_id }}
                      </BaseText>
                      <div class="ml-auto">
                        <!-- Either Chevron Icon or New Checkmark Icon -->
                        <NewCheckmarkIcon v-if="isCurrentRelatedAd(ad)" class="text-icon-normal" style="width: 16px; height: 16px;" />
                        <ChevronIcon v-else class="text-icon-normal transform -rotate-90" style="width: 16px; height: 16px;" />
                      </div>
                    </div>
                  </div>
                </transition>
              </div>

              <!-- Vertical line spacer -->
              <div v-if="currentAd.ad_id" class="w-px h-4 bg-neutral-50" :class="mobileView ? 'mx-auto' : 'mx-1'" />

              <!-- Embed button -->
              <button class="group flex items-center gap-1.5 pl-2 py-1.5 pr-2.5 rounded-md" :disabled="!showEmbed"
              :class="showEmbed ? 'transition-colors hover:bg-neutral-25' : 'bg-neutral-25 cursor-default'"
              v-clipboard:copy="getEmbedCode" v-clipboard:success="handleEmbedClipboardCopy">
                <EmbedIcon :class="showEmbed ? 'transition-colors text-icon-normal group-hover:text-icon-muted' : 'text-icon-disabled'" />
                <BaseText type="label" size="sm" :class="showEmbed ? 'transition-colors text-text-muted group-hover:text-black' : 'text-text-disabled'">
                  Embed
                </BaseText>
              </button>
              <!-- Share button -->
              <button class="action-btn flex items-center gap-1.5 pl-2 py-1.5 pr-2.5 rounded-md" :class="{'enabled': !shareDisabled}"
              :disabled="shareDisabled" v-clipboard:copy="getShareLink" v-clipboard:success="handleClipboardCopy">
                <ShareIcon :class="shareDisabled ? 'text-text-disabled' : 'text-white'" />
                <BaseText type="label" size="sm" :class="shareDisabled ? 'text-text-disabled' : 'text-white'">
                  Share
                </BaseText>
              </button>
            </div>
            <!-- Next ad button -->
            <button v-if="!mobileView" class="cycle-btn flex items-center gap-1.5 pl-3 py-1.5 pr-1.5 rounded-md" 
            :class="!nextAdDisabled ? 'transition-colors hover:bg-neutral-25' : 'cursor-default opacity-60'"
            :disabled="nextAdDisabled" @click="nextAdvertisement">
              <BaseText type="label" size="sm" class="transition-colors" :class="!nextAdDisabled ? 'text-text-muted' : 'text-text-disabled'">
                Next
              </BaseText>
              <BaseLoadingSpinnerCircle v-if="loadingNewAds" class="text-icon-normal" />
              <svg v-else xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none" class="transition-colors"
              :class="!nextAdDisabled ? 'text-icon-normal' : 'text-icon-disabled'">
                <path d="M8.33331 13.3327L11.0774 10.5886C11.4028 10.2632 11.4028 9.73553 11.0774 9.41009L8.33331 6.66602" 
                stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
          </div>
          <!-- AD CARD -->
          <div ref="advertisement-card-area" class="relative w-full flex-grow min-h-0 overflow-y-scroll scrollbar-hide" :class="mobileView ? 'py-2.5' : 'py-6'">
            <div class="max-w-sm z-10 h-max mx-auto" :class="{'centered-card-wrapper': !enableAdCardScroll}">
              <LibraryCard 
                :advertisement="currentAd || advertisement"
                :discovery="discovery"
                :shared-board="sharedBoard"
                :shared-brief="sharedBrief"
                :init-ad-copy-expanded="withAdCopyExpanded"
                in-ad-details-drawer
                all-ad-settings
                hide-boards
                no-drawer
                hide-copy-button
                relative-media
                class="w-max max-w-sm"
                ref="advertisement-card"
                @carouselIndex="carouselIndex = $event"
                @expandCopy="determineAdCardOverflow"
                @loaded-content="determineAdCardOverflow"
              />
            </div>
          </div>
          <div v-if="currentAd?.type === 'dco' && currentAd?.cards?.length && !mobileView"
          class="absolute left-4 transform -translate-y-1/2 z-50" style="top: calc(50% - 26px)">
            <DCOPreviewSelector
              :cards="currentAd.cards"
              :carouselIndex.sync="carouselIndex"
            />
          </div>
        </div>

        <!-- RIGHT-HAND INFO CARD -->
        <div class="info-card flex flex-col mt-1 mr-1 bg-white overflow-y-scroll scrollbar-hide bg-transparent"
        :style="{height: mobileView ? 'auto' : 'calc(100vh - 80px)', width: mobileView ? '100%' : '448px'}" style="border-radius: 20px">
          <!-- Tab Switcher -->
          <div data-tour="drawer-sidebar" class="flex items-center gap-2 p-4 border-b border-border-normal" :class="mobileView ? 'justify-center' : 'justify-start'">
            <button data-tour="drawer-tab-details" class="group px-2 py-1.5 rounded-md transition-colors" :class="{'hover:bg-neutral-10': sidebarView !== 'details'}" 
            @click="sidebarView = 'details'">
              <BaseText type="label" size="sm" class="transition-colors" :class="sidebarView === 'details' ? 'text-text-muted' : 'text-text-subdued group-hover:text-text-muted'">
                Details
              </BaseText>
            </button>
            <button v-if="aiAnalysisEnabled" data-tour="drawer-tab-ai" class="group px-2 py-1.5 flex items-center gap-1.5 rounded-md transition-colors whitespace-nowrap" 
            :class="{'hover:bg-neutral-10': sidebarView !== 'ai-analysis'}" @click="sidebarView = 'ai-analysis'">
              <BaseText type="label" size="sm" class="transition-colors" :class="sidebarView === 'ai-analysis' ? 'text-text-muted' : 'text-text-subdued group-hover:text-text-muted'">
                AI Analysis
              </BaseText>
              <!-- <div class="px-1.5 py-0.5 rounded-full" :class="`bg-primary-${getTheme}-10`">
                <BaseText type="label" size="xs" :class="`text-primary-${getTheme}-300`">New</BaseText>
              </div> -->
            </button>
            <button v-if="transcriptEnabled" data-tour="drawer-tab-transcript" class="group px-2 py-1.5 rounded-md transition-colors" :class="{'hover:bg-neutral-10': sidebarView !== 'transcript'}" 
            @click="sidebarView = 'transcript'">
              <BaseText type="label" size="sm" class="transition-colors" :class="sidebarView === 'transcript' ? 'text-text-muted' : 'text-text-subdued group-hover:text-text-muted'">
                Transcript
              </BaseText>
            </button>
            <button v-if="commentsEnabled" class="group flex items-center gap-1 px-2 py-1.5 rounded-md transition-colors" 
            :class="{'hover:bg-neutral-10': sidebarView !== 'comments'}" @click="sidebarView = 'comments'">
              <BaseText type="label" size="sm" class="transition-colors" :class="sidebarView === 'comments' ? 'text-text-muted' : 'text-text-subdued group-hover:text-text-muted'">
                Comments
              </BaseText>
              <BaseText v-if="comments.length" type="label" size="xs" :class="{'group-hover:bg-neutral-50': sidebarView !== 'comments'}"
              class="text-text-muted px-1.5 py-0.5 rounded-full bg-neutral-25 transition-colors">
                {{ comments.length }}
              </BaseText>
            </button>
          </div>
          <!-- DETAILS VIEW -->
          <div v-if="sidebarView === 'details'" class="flex-grow flex flex-col w-full min-h-0">
            <!-- Organization Section (Ad saving, board select, tags, rating) -->
            <div v-if="!sharedBoard && getUser.user_id" class="p-4 border-b border-border-normal"
            :class="discovery ? 'flex flex-col gap-2' : 'details-organization-grid'">
              <div v-if="!discovery && !mobileView" class="self-center">
                <BaseText type="body" size="sm" class="text-text-normal">
                  Boards
                </BaseText>
              </div>
              <div data-tour="drawer-save" class="w-full" :class="mobileView ? 'mb-2 col-span-2' : 'mb-0 col-auto'">
                <SaveAdDropdown 
                  :advertisement="currentAd"
                  :isSavedAd="!discovery"
                  :isEditMode="!discovery"
                  :externalAdSavedId="adCardSavedId"
                  noHoverOpener
                  coloredByDefault
                  @adSaved="$emit('adSaved', $event)"
                  @updateBoards="handleUpdateAdBoardsLocal($event)"
                  @dropdownOpened="handleBoardsDropdownOpened(true)"
                  @dropdownClosed="handleBoardsDropdownOpened(false)"
                />
              </div>
              <div v-if="discovery" class="flex items-center gap-2 p-1.5 rounded-md bg-neutral-25">
                <InfoResponseIcon class="text-icon-normal" />
                <BaseText type="body" size="sm" class="text-text-muted">
                  Save to Swipe File to share, rate, and add tags.
                </BaseText>
              </div>
              <!-- <button v-if="adMedia" class="w-full gpt-ad-btn flex items-center gap-1.5 justify-center" @click="showGptAdModal = true">
                <GptIcon/>
                <BaseText class="text-text-muted" type="label" size="sm">Make this Ad</BaseText>
              </button> -->
              <div v-if="!discovery && currentAd.id" class="self-center py-2">
                <BaseText type="body" size="sm" class="text-text-normal">
                  Rating
                </BaseText>
              </div>
              <div v-if="!discovery && currentAd.id" class="pl-2 self-center">
                <RatingSelector
                  :adRating="currentAd.rating || 0" 
                  @updateAdRating="handleUpdateAdRating"
                />
              </div>
              <div v-if="!discovery" class="self-start py-2">
                <BaseText type="body" size="sm" class="text-text-normal">
                  Tags
                </BaseText>
              </div>
              <div v-if="!discovery" class="self-center py-0.5">
                <Tags 
                  :advertisement="currentAd" 
                  @tagDeleted="$emit('tagDeleted', $event)"
                  @update:tableViewTags="forwardTagEdits"
                />
              </div>
            </div>
            <!-- Ad details (additional ad information / metadata) -->
            <div data-tour="drawer-metadata" class="scrollable-section flex-grow w-full min-h-0">
              <AdDetails 
                :advertisement="currentAd"
                :createdByUser="createdBy"
                :createdByLoading="loaders.createdBy"
                :assetMetadata="assetMetadata"
                :discovery="discovery"
                :carouselIndex="carouselIndex"
                :hasAiAnalysis="aiAnalysisEnabled"
                :enableBrandRouting="!sharedBoard"
                :sidebarView.sync="sidebarView"
                :mobileView="mobileView"
              />
            </div>
          </div>
          <!-- AI ANALYSIS VIEW -->
          <div v-if="sidebarView === 'ai-analysis'" data-tour="drawer-ai-analysis" class="scrollable-section flex-grow w-full min-h-0">
            <AIAnalysis 
              :advertisement="currentAd"
              :mobileView="mobileView"
            />
          </div>
          <!-- TRANSCRIPT VIEW -->
          <div v-if="sidebarView === 'transcript'" data-tour="drawer-transcript" class="flex-grow w-full min-h-0">
            <Transcript 
              :advertisement="currentAd"
              :carouselIndex="carouselIndex"
              :assetMetadata="assetMetadata"
              :mobileView="mobileView"
              :isDiscovery="discovery"
              @refreshTranscript="refreshTranscript"
              @playProductIntro="playAdVideoAtTimestamp"
            />
          </div>
          <!-- COMMENTS VIEW -->
          <div v-if="sidebarView === 'comments'" class="flex-grow w-full min-h-0">
            <Comments v-if="currentAd"
              :can-modify-board="canModifyBoard && !discovery && canReadCommentsDirectly"
              :advertisement="currentAd"
              :comments.sync="comments"
              :public-users="commentUsers"
              :commentsLoading="commentsLoading"
            />
          </div>

          <!-- Ad Action Buttons -->
          <div data-tour="drawer-downloads" class="w-full flex items-center py-4 pl-4 border-t border-border-normal"
          :class="advertisementActions.length > 2 ? 'gap-3' : 'gap-5'">
            <!-- Delete Button -->
            <button v-if="advertisementActions.includes('delete')" 
            @click="handleDeleteAdvertisement" :disabled="loadingDelete"
            class="group flex items-center gap-1.5 px-2 py-1.5 rounded-md transition"
            :class="loadingDelete ? 'opacity-60' : 'opacity-100 hover:bg-neutral-25'">
              <BaseLoadingSpinnerCircle v-if="loadingDelete" class="text-icon-normal" />
              <DeleteV2Icon v-else class="text-icon-normal transition-colors group-hover:text-icon-muted" />
              <BaseText type="label" size="sm" class="text-text-muted transition-colors group-hover:text-black">
                Delete
              </BaseText>
            </button>
            <!-- Report Button (TODO: Implement and add conditionals) -->
            <button v-if="advertisementActions.includes('report')" @click="showReportModal = true"
            class="group flex items-center gap-1.5 px-2 py-1.5 rounded-md transition-colors hover:bg-neutral-25">
              <WarningIcon class="text-icon-normal transition-colors group-hover:text-icon-muted" />
              <BaseText type="label" size="sm" class="text-text-muted transition-colors group-hover:text-black">
                Report
              </BaseText>
            </button>
            <!-- Alternate download (thumbnail / collection) -->
            <button v-if="advertisementActions.includes('download-alt')" :disabled="loadingAltDownload"
            class="group flex items-center gap-1.5 px-2 py-1.5 rounded-md transition"
            :class="loadingAltDownload ? 'opacity-60 cursor-default' : 'opacity-100 hover:bg-neutral-25'"
            @click="() => {downloadAdvertisementAsset(true)}">
              <!-- Loading icon logic -->
              <BaseProgressIndicatorRing v-show="loadingAltDownload && retrievingDownloadData"
              ref="progress-ring-alt" class="text-icon-normal" :radius="10" />
              <BaseLoadingSpinnerCircle v-if="loadingAltDownload && !retrievingDownloadData" 
              class="text-icon-normal" />
              <DownloadIcon v-if="!loadingAltDownload"
              class="text-icon-normal transition-colors group-hover:text-icon-muted" />
              <!-- Alt download button text -->
              <BaseText type="label" size="sm" class="text-text-muted transition-colors "
              :class="{'group-hover:text-black': !loadingAltDownload}">
                {{ getAltDownloadLabel() }}
              </BaseText>
            </button>
            <!-- Primary asset download -->
            <div v-if="advertisementActions.includes('download')" class="relative">
              <button :disabled="loadingDownload"
              class="group flex items-center gap-1.5 px-2 py-1.5 rounded-md transition-colors"
              :class="[loadingDownload ? 'opacity-60 cursor-default' : 'opacity-100 hover:bg-neutral-25', {'bg-neutral-25': showDownloadDropdown}]"
              @click="() => {handlePrimaryAction()}">
                <!-- Loading icon logic -->
                <BaseProgressIndicatorRing v-show="loadingDownload && retrievingDownloadData"
                ref="progress-ring" class="text-icon-normal" :radius="10" />
                <BaseLoadingSpinnerCircle v-if="loadingDownload && !retrievingDownloadData" 
                class="text-icon-normal" />
                <DownloadIcon v-if="!loadingDownload" 
                class="text-icon-normal transition-colors group-hover:text-icon-muted" />
                <!-- Download button text -->
                <BaseText type="label" size="sm" class="text-text-muted transition-colors capitalize"
                :class="{'group-hover:text-black': !loadingDownload}">
                  {{ getDownloadLabel() }}
                </BaseText>
              </button>
              <!-- Extra options dropdown (if BaseCarousel with video thumbnail) -->
              <transition>
                <div v-if="showDownloadDropdown" v-on-clickaway="() => {showDownloadDropdown = false}"
                class="download-dropdown flex flex-col gap-0.5 p-1 bg-white">
                  <button class="group flex items-center gap-1.5 px-2 py-1.5 rounded-md transition-colors hover:bg-neutral-25"
                  @click="handleDropdownSelection('thumbnail')">
                    <ImageFormatIcon class="text-icon-normal transition-colors group-hover:text-icon-muted" />
                    <BaseText type="label" size="sm" class="text-text-normal transition-colors group-hover:text-text-muted">
                      Thumbnail
                    </BaseText>
                  </button>
                  <button class="group flex items-center gap-1.5 px-2 py-1.5 rounded-md transition-colors hover:bg-neutral-25"
                  @click="handleDropdownSelection('video')">
                    <VideoFormatIcon class="text-icon-normal transition-colors group-hover:text-icon-muted" />
                    <BaseText type="label" size="sm" class="text-text-normal transition-colors group-hover:text-text-muted">
                      Video
                    </BaseText>
                  </button>
                </div>
              </transition>
            </div>
          </div>
        </div>
        <!-- Hidden video element for loading metadata -->
        <video v-if="['video', 'dco'].includes(currentAd?.type) && (currentAd?.video || currentAd?.cards?.[0]?.video)" 
        ref="video" style="display: none;"
        preload="metadata" @loadedmetadata="onVideoMetadataLoaded">
          <source :src="currentAd.video" type="video/mp4" />
        </video>
      </div>
      <!-- Modals -->
      <ReportAdModal 
        v-if="showReportModal"
        :advertisement="currentAd"
        @close="showReportModal = false"
      />
      <GptAdModal
        v-if="showGptAdModal"
        :img-url="adMedia"
        @close="showGptAdModal = false"
      />
    </template>
  </BaseDrawer>
</template>

<script>
import _ from 'lodash'
import { mapGetters, mapMutations } from 'vuex'
import firebase from '@/api/config/FirebaseConfig'
import detectAdBlock from '../../utils/adblock'
import FirebaseAPI from '@/api/firebase'
import { sendEventSocket } from '../../api/sockets'
import JSZip from 'jszip'
import { mixin as clickaway } from 'vue-clickaway2'
import moment from 'moment'
import ForeplayAPI from '@/api/foreplayServer'
import { getAdEffectiveEndDate } from '@/utils/dateUtils'

// Components
import Tags from './DetailsDrawerComponents/Tags'
import RatingSelector from './DetailsDrawerComponents/RatingSelector'
import SaveAdDropdown from './SaveAdDropdown/SaveAdDropdown.vue'
import AdDetails from './DetailsDrawerComponents/AdDetails.vue'
import AIAnalysis from './DetailsDrawerComponents/AIAnalysis.vue'
import Transcript from './DetailsDrawerComponents/Transcript.vue'
import Comments from './DetailsDrawerComponents/Comments'
import Screenshots from './DetailsDrawerComponents/Screenshots'
import LibraryCard from '../library/LibraryCard.vue'
import DCOPreviewSelector from './DetailsDrawerComponents/DCOPreviewSelector.vue'
import DiscoverySave from './DetailsDrawerComponents/DiscoverySave.vue'
import ReportAdModal from './DetailsDrawerComponents/ReportAdModal.vue'
import GptAdModal from './DetailsDrawerComponents/GptAdModal.vue'

// Icons
import OpenExternalIcon from '../globals/Icons/OpenExternalIcon.vue'
import EmbedIcon from '../globals/Icons/EmbedIcon.vue'
import ShareIcon from '../globals/Icons/ShareIcon.vue'
import InfoResponseIcon from '../globals/Icons/ResponseAlertIcons/InfoResponseIcon.vue'
import DeleteV2Icon from '../globals/Icons/DeleteV2Icon.vue'
import DownloadIcon from '../globals/Icons/DownloadIcon.vue'
import WarningIcon from '../globals/Icons/WarningIcon.vue'
import VideoFormatIcon from '../globals/Icons/FilterIcons/VideoFormatIcon.vue'
import ImageFormatIcon from '../globals/Icons/FilterIcons/ImageFormatIcon.vue'
import GptIcon from '../globals/Icons/GptIcon.vue'
import SameCreativeIcon from '../globals/Icons/SameCreativeIcon.vue'
import ChevronIcon from '../globals/Icons/ChevronIcon.vue'
import NewCheckmarkIcon from '../globals/Icons/NewCheckmarkIcon.vue'
import { isManualUpload } from '@/utils/adFunctions'

export default {
  name: 'AdvertisementDetailsDrawer',
  mixins: [clickaway],
  components: {
    Tags,
    RatingSelector,
    SaveAdDropdown,
    AdDetails,
    AIAnalysis,
    Transcript,
    Comments,
    Screenshots,
    DiscoverySave,
    GptAdModal,
    ReportAdModal,
    LibraryCard,
    DCOPreviewSelector,
    OpenExternalIcon,
    EmbedIcon,
    ShareIcon,
    InfoResponseIcon,
    DeleteV2Icon,
    DownloadIcon,
    WarningIcon,
    VideoFormatIcon,
    ImageFormatIcon,
    GptIcon,
    SameCreativeIcon,
    ChevronIcon,
    NewCheckmarkIcon
  },
  props: {
    allAds: {
      type: Array,
      default: () => []
    },
    advertisement: {
      type: Object,
      default: () => {}
    },
    sharedBoard: {
      type: Boolean,
      default: () => false
    },
    sharedBrief: {
      type: Boolean,
      default: () => false
    },
    discovery: {
      type: Boolean,
      default: () => false
    },
    loading: {
      type: Boolean,
      default: () => false
    },
    adCardSavedId: {
      type: String,
      default: ''
    },
    allAdsLoaded: {
      type: Boolean,
      default: () => false
    },
    withAdCopyExpanded: {
      type: Boolean,
      default: () => false
    },
    isChildDrawer: {
      type: Boolean,
      default: () => false
    }
  },
  data () {
    return {
      // Data States
      currentAd: {},
      previousAd: {},
      nextAd: {},
      selectedBoards: [],
      selectedRating: [],
      selectedTags: [],
      createdBy: {},
      niches: [],
      betaUser: false,
      assetMetadata: {},
      comments: [],
      commentUsers: null,
      carouselIndex: 0,

      // UI States

      loaders: {
        createdBy: false,
      },
      sidebarView: 'details',
      loadingNewAds: false,
      loadingTranscription: false,
      adBlockedDetected: false,
      loadingDelete: false,
      commentsLoading: false,
      stars: [{ icon: '⭐⭐⭐⭐⭐', value: 5 }, { icon: '⭐⭐⭐⭐', value: 4 }, { icon: '⭐⭐⭐', value: 3 }, { icon: '⭐⭐', value: 2 }, { icon: '⭐', value: 1 }],
      enableAdCardScroll: false,
      showReportModal: false,
      showDownloadDropdown: false,
      showGptAdModal: false,
      noCommentsImgSrc: require('../../assets/images/break-the-silence.png'),
      mobileView: window.innerWidth < 1200,

      // Tooltip states
      showSameCreativeTooltip: false,

      // Download States
      loadingDownload: false,
      loadingAltDownload: false,
      retrievingDownloadData: false,
    }
  },
  computed: {
    ...mapGetters('AuthModule', ['getUser', 'getTeam', 'getUserName']),
    ...mapGetters('BoardsModule', ['getBoards']),
    ...mapGetters('TagsModule', ['getTags']),
    ...mapGetters('MiscModule', ['getTheme']),
    adMedia () {
      if (!this.currentAd.id) return ''
      const cardImage = this.currentAd?.cards?.[this.carouselIndex]
      return this.currentAd?.image || cardImage?.image
    },
    showEmbed () {
      return !(
        this.currentAd.publisher_platform?.includes('landing page') 
        || ['carousel', 'dco', 'dpa'].includes(this.currentAd?.type)
      )
    },
    relatedAdsList () {
      if (this.currentAd?.cards?.length) {
        return this.currentAd?.cards[this.carouselIndex]?.relatedAds
      }
      return this.currentAd?.relatedAds
    },
    getInstagramUrl () {
      return `https://www.instagram.com/p/${this.currentAd?.instagramAttributes?.shortCode}`
    },
    advertisementActions () {
      // The list of action buttons to render at the bottom of the info card
      const actions = []
      if (!this.discovery && !this.sharedBoard) actions.push('delete')
      if (this.discovery) actions.push('report')
      if (
        (this.currentAd?.type === 'video' && this.currentAd.thumbnail)
        || (['carousel', 'dco'].includes(this.currentAd?.type) && this.currentAd?.cards?.length)
      ) {
        actions.push('download-alt')
      }
      if (
        (this.currentAd?.type === 'video' && this.currentAd.video)
        || (this.currentAd?.type === 'image' && this.currentAd.image)
        || (this.currentAd?.type === 'carousel' && this.currentAd?.cards?.length)
        || (this.currentAd?.type === 'dco' && this.currentAd?.cards?.length)
      ) {
        actions.push('download')
      }
      return actions
    },
    canModifyBoard () {
      return !this.sharedBoard && !this.sharedBrief
    },
    getEmbedCode () {
      const randomlyGeneratedId = Math.random().toString(36).substring(7)
      return `<iframe id="${randomlyGeneratedId}" src="${
        process.env.VUE_APP_API_URL
      }/embed/${
        this.currentAd.id
      }" allowtransparency="true" scrolling="no" frameborder="0" width="100%"></iframe><script>function resizeIframe(e){var t=document.getElementById("${randomlyGeneratedId}");e.data&&e.data.height&&(t.style.height=e.data.height+"px")}window.addEventListener("message",resizeIframe,!1);${'</scr'}ipt>`
    },
    getShareLink () {
      let path = this.$route.path
      let pathName
      path = path.split('/')
      if (path[1] === 'library') {
        pathName = 'Swipe FIles'
      } else if (path[1] === 'boards') {
        pathName = 'Board'
      } else if (path[1] === 'discovery') {
        pathName = 'Discovery'
      }
      return (
        process.env.VUE_APP_BASE_URL +
        '/share/ads/' +
        encodeURIComponent(this.currentAd.id) +
        '?user=' +
        encodeURIComponent(this.getUser.user_id)
      )
    },
    shareDisabled () {
      return this.discovery || !this.canModifyBoard
    },
    onLastAd () {
      return this.allAdsLoaded && this.allAds.indexOf(this.currentAd) === this.allAds.length - 1
    },
    nextAdDisabled () {
      return this.onLastAd || ((this.loadingNewAds || this.loading) && !this.nextAd)
    },
    aiAnalysisEnabled () {
      return !isManualUpload(this.currentAd)
        && ['image', 'video', 'carousel', 'dco'].includes(this.currentAd?.type)
    },
    transcriptEnabled () {
      return this.currentAd?.type === 'video'
        || (this.currentAd?.type === 'dco' && this.currentAd?.cards?.length && this.currentAd.cards[this.carouselIndex].video)
    },
    commentsEnabled () {
      return !this.discovery && !this.mobileView
    },
    canReadCommentsDirectly () {
      const uid = this.getUser?.user_id
      if (!uid || !this.currentAd) return false

      return this.currentAd.created_by === uid ||
        (!!this.currentAd.teamId && this.currentAd.teamId === this.getTeam?.id)
    }
  },
  watch: {
    getBoards (newValue, oldValue) {
      if (newValue.length !== oldValue.length) {
        this.refreshBoards()
      }
    },
    allAds () {
      this.loadingNewAds = false
    },
    allAdsLoaded () {
      this.loadingNewAds = false
    },
    carouselIndex () {
      const card = this.$refs['advertisement-card']
      if (card) {
        card.setCarouselIndex(this.carouselIndex)
      }
      if (this.sidebarView === 'transcript' && !this.transcriptEnabled) {
        this.sidebarView = 'details'
      }
      // Close the related ads dropdown when carousel index changes
      this.showSameCreativeTooltip = false
      this.$nextTick(() => { this.determineAdCardOverflow() })
    },
    currentAd: {
      async handler (newValue, oldValue) {
        // When navigating through ads, we may need to update the sidebar view
        if (
          (this.sidebarView === 'transcript' && !this.transcriptEnabled)
          || (this.sidebarView === 'comments' && !this.commentsEnabled)
          || (this.sidebarView === 'ai-analysis' && !this.aiAnalysisEnabled)
        ) {
          this.sidebarView = 'details'
        }

        this.carouselIndex = 0

        // Fetch the comments for the advertisement
        if (this.commentsEnabled) {
          this.fetchAdComments()
        }

        // Extract metadata from ad asset
        if (this.currentAd?.type === 'image' && this.currentAd.image) {
          const image = new Image()
          image.src = this.currentAd.image
          if (image.complete) { // In case the image was previously cached
            this.assetMetadata = { width: image.width, height: image.height }
          } else { // Otherwise, wait for the image to load
            image.onload = () => {
              this.assetMetadata = { width: image.width, height: image.height }
            }
          }
        } else if (
          (this.currentAd?.type === 'video' && this.currentAd.video)
          || (this.currentAd?.type === 'dco' && this.currentAd?.cards?.length && this.currentAd.cards[0].video)
        ) {
          this.$nextTick(() => {
            const video = this.$refs.video
            video.pause()
            video.src = ''
            video.src = this.currentAd.video || this.currentAd.cards[0].video
            video.load()
            // The metadata computations are perfomed when the <video> element emits the 'loadedmetadata' event
          })
        }

        this.$nextTick(() => { this.determineAdCardOverflow() })

        if (newValue.id && newValue.id !== oldValue.id) {
          sendEventSocket('adViewed', { adId: this.currentAd.id, brandId: this.currentAd.brandId })
        }
      },
      immediate: true
    }
  },
  async mounted () {
    // Configure listener for mobile view
    this.mediaQuery = window.matchMedia('(max-width: 1100px)')
    this.mobileView = this.mediaQuery.matches
    this.mediaQuery.addEventListener('change', this.handleBreakpointChange)

    this.SET_AD_PLAYING('')
    this.currentAd = this.advertisement
    this.previousAd = this.allAds[(this.allAds.indexOf(this.advertisement) - 1)]
    this.nextAd = this.allAds[(this.allAds.indexOf(this.advertisement) + 1)]

    this.determineAdCardOverflow()

    const adTags = this.currentAd.tags || []
    this.selectedTags = await FirebaseAPI.Tags.getTagsArray(adTags)

    // Check beta user
    const betaIds = await FirebaseAPI.Beta.getBetaUserIds()
    const mappedIds = betaIds.map(id => id.id)

    if (mappedIds.includes(this.getUser.user_id)) {
      this.betaUser = true
    }

    if (this.currentAd?.rating) {
      this.selectedRating = [this.stars.find(star => this.currentAd?.rating === star.value)]
    } else {
      this.selectedRating = []
    }
    setTimeout(() => {
      this.refreshBoards()
    }, 100)

    this.detectAdBlock().then((detected) => {
      if (detected) {
        this.adBlockedDetected = true
      }
    })

    // Cache the empty comments image
    const image = new Image()
    image.src = this.noCommentsImgSrc

    await this.getCreatedByUser()
    await this.getNiches()
  },
  beforeDestroy () {
    this.mediaQuery.removeEventListener('change', this.handleBreakpointChange)
  },
  methods: {
    ...mapMutations('AuthModule', ['SET_USER']),
    ...mapMutations('AdvertisementsModule', ['SET_AD_PLAYING']),
    ...mapMutations('MiscModule', ['SET_PREVENT_DRAWER_ESCAPE_KEY']),
    detectAdBlock,
    getAdEffectiveEndDate,
    isCurrentRelatedAd (ad) {
      return Boolean(ad?.id && this.currentAd?.id && ad.id === this.currentAd.id)
    },

    // ================================================================================
    // ================================= DATA METHODS =================================
    // ================================================================================

    async handleUpdateAdRating (rating) {
      try {
        await FirebaseAPI.Advertisements.update(this.currentAd.id, { rating: rating })
        const copyAd = { ...this.currentAd, rating: rating }
        this.$emit('updateAds', [copyAd])
        this.currentAd = copyAd

        this.$showAlert({
          message: 'Ad rating updated',
          type: 'success'
        })
      } catch (error) {
        console.error("Couldn't update ad rating", error)
        this.$showAlert({
          message: error,
          type: 'error'
        })
      }
    },
    handleUpdateAdTags: _.debounce(async function () {
      try {
        const payload = {
          tags: this.selectedTags.map((e) => e.id)
        }

        await FirebaseAPI.Advertisements.update(this.currentAd.id, payload)

        // Update Parent Ad
        const copyAd = { ...this.currentAd, tags: payload.tags }
        this.$emit('updateAds', [copyAd])
        this.currentAd = copyAd

        this.$showAlert({
          message: 'Ad tags updated',
          type: 'success'
        })
        this.$emit('update')
      } catch (e) {
        this.$showAlert({
          message: e,
          type: 'error'
        })
      }
    }, 1000),
    handleUpdateAdBoardsLocal (boardIds) {
      const copyAd = { ...this.currentAd, board_ids: boardIds }
      this.$emit('updateAds', [copyAd])
      this.currentAd = copyAd
    },
    async loadAdvertisement (adId) {
      const currentAdIndex = this.allAds.indexOf(this.currentAd) || 0

      try {
        const newAd = await ForeplayAPI.Ads.getUniqueAdById(adId)
        this.currentAd = newAd
        this.previousAd = this.allAds[(currentAdIndex - 1)]
        this.nextAd = this.allAds[(currentAdIndex + 1)]
        this.refreshBoards()
      } catch (error) {
        console.error("Failed to load advertisement:", error)
      }
    },
    async nextAdvertisement () {
      this.SET_AD_PLAYING('')
      // Close the related ads dropdown when navigating to next ad
      this.showSameCreativeTooltip = false
      if (this.allAds.indexOf(this.currentAd) === this.allAds.length - 1) {
        console.log('get more ads')
        this.loadingNewAds = true
        this.$emit('getMoreAds')
        return
      }
      this.loadingNewAds = false
      if (this.nextAd) {
        this.currentAd = this.nextAd
      } else {
        this.currentAd = this.allAds[(this.allAds.indexOf(this.currentAd) + 1)]
      }
      this.previousAd = this.allAds[(this.allAds.indexOf(this.currentAd) - 1)]
      this.nextAd = this.allAds[(this.allAds.indexOf(this.currentAd) + 1)]
      this.refreshBoards()
      if (this.currentAd?.rating) {
        this.selectedRating = [this.stars.find(star => this.currentAd?.rating === star.value)]
      } else {
        this.selectedRating = []
      }
      const adTags = this.currentAd.tags || []
      this.selectedTags = await FirebaseAPI.Tags.getTagsArray(adTags)
    },
    async previousAdvertisement () {
      this.SET_AD_PLAYING('')
      // Close the related ads dropdown when navigating to previous ad
      this.showSameCreativeTooltip = false
      if (this.allAds.indexOf(this.currentAd) === 0) {
        return
      }
      this.currentAd = this.previousAd
      this.previousAd = this.allAds[(this.allAds.indexOf(this.currentAd) - 1)]
      this.nextAd = this.allAds[(this.allAds.indexOf(this.currentAd) + 1)]
      this.refreshBoards()
      if (this.currentAd?.rating) {
        this.selectedRating = [this.stars.find(star => this.currentAd?.rating === star.value)]
      } else {
        this.selectedRating = []
      }
      const adTags = this.currentAd.tags || []
      this.selectedTags = await FirebaseAPI.Tags.getTagsArray(adTags)
    },
    async getCreatedByUser () {
      this.createdBy = {}
      if (!this.currentAd?.created_by) return

      this.loaders.createdBy = true
      try {
        const user = await FirebaseAPI.Users.getPublicById(this.currentAd.created_by)

        this.createdBy = {
          avatar: user?.avatar || null,
          name: user?.name || 'Former teammate'
        }
      } catch (error) {
        console.error('Unable to fetch ad creator', error)
        this.createdBy = {
          avatar: null,
          name: 'Unknown User'
        }
      } finally {
        this.loaders.createdBy = false
      }
    },
    async getNiches () {
      // Setup
      try {
        const db = firebase.firestore()

        const snapshot = await db.collection('niches').get()

        const newDocs = snapshot.docs?.map((doc) => {
          const docData = {
            ...(doc.data() || [])
          }

          return { name: docData.niche, icon: docData.emoji }
        })

        const sortedDocs = newDocs.sort((a, b) => (a.name > b.name ? 1 : -1))
        const otherIndex = sortedDocs.findIndex((d) => d.name === 'Other')
        const otherNiche = sortedDocs.splice(otherIndex, 1)

        sortedDocs.push(otherNiche[0])

        this.niches = sortedDocs
      } catch (e) {
        console.log(e)
      }
    },
    async fetchAdComments () {
      if (!this.currentAd?.id) return
      // Load the comments for the current ad
      this.commentsLoading = true
      try {
        if (!this.canReadCommentsDirectly) {
          const { comments, users } = await ForeplayAPI.Ads.getPublicAdComments(this.currentAd.id)
          this.commentUsers = users
          this.comments = comments
          return
        }

        const comments = await FirebaseAPI.Comments.getCommentsByAdId(this.currentAd.id)
        this.commentUsers = null
        this.comments = comments.sort((a, b) => a.created_at > b.created_at ? 1 : -1)
      } catch (error) {
        console.error("Failed to load ad comments:", error)
        this.commentUsers = null
        this.comments = []
      } finally {
        this.commentsLoading = false
      }
    },
    async refreshTranscript (updatedTranscript) {
      // Locally update the transcript
      this.currentAd = {
        ...this.currentAd,
        ...updatedTranscript
      }
    },
    // ================== DOWNLOAD METHODS ==================

    handlePrimaryAction () {
      if (
        ['carousel', 'dco', 'dpa'].includes(this.currentAd?.type) 
        && this.currentAd?.cards[this.carouselIndex].thumbnail
      ) {
        this.showDownloadDropdown = !this.showDownloadDropdown
      } else {
        this.downloadAdvertisementAsset()
      }
    },
    handleDropdownSelection (type) {
      const animateProgress = this.$refs['progress-ring'].updateProgress
      const filename = `${this.currentAd.type}-${this.carouselIndex}-${type}-${this.currentAd.id}`
      const url = type === 'thumbnail' 
        ? this.currentAd.cards[this.carouselIndex].thumbnail 
        : this.currentAd.cards[this.carouselIndex].video
      this.showDownloadDropdown = false
      this.executeDownload(url, filename, animateProgress).catch((error) => {
        console.log('Asset fetch request was rejected', error)
        this.executeBackupDownload(url, error)
      })
    },
    async downloadAdvertisementAsset (alternate = false) {
      // Reset progress states and flag loading
      this.targetDownloadProgress = 0
      this.renderedDownloadProgress = 0
      alternate ? this.loadingAltDownload = true : this.loadingDownload = true

      // Get the progress animation function
      const refStr = alternate ? 'progress-ring-alt' : 'progress-ring'
      const animateProgress = this.$refs[refStr].updateProgress

      // Configure the download based on the advertisement and type
      let url = null
      let filename = null
      try {
        if (this.currentAd?.type === 'image') {
          filename = `image-${this.currentAd.id}`
          url = this.currentAd.image
        } else if (this.currentAd?.type === 'video') {
          filename = `${alternate ? 'thumbnail' : 'video'}-${this.currentAd.id}`
          url = alternate ? this.currentAd.thumbnail : this.currentAd.video
        } else if (this.currentAd?.type === 'carousel') {
          if (alternate) {
            filename = `carousel-${this.currentAd.id}`
            await this.executeCollectionDownload(this.currentAd.cards, filename, animateProgress)
            return
          }
          filename = `carousel-${this.carouselIndex}-${this.currentAd.id}`
          url = this.currentAd.cards[this.carouselIndex].image
            || this.currentAd.cards[this.carouselIndex].video
        } else if (this.currentAd?.type === 'dco') {
          if (alternate) {
            filename = `dco-${this.currentAd.id}`
            await this.executeCollectionDownload(this.currentAd.cards, filename, animateProgress)
            return
          }
          filename = `dco-${this.carouselIndex}-${this.currentAd.id}`
          url = this.currentAd.cards[this.carouselIndex].image
            || this.currentAd.cards[this.carouselIndex].video
        }
        await this.executeDownload(url, filename, animateProgress)

      } catch (error) {
        this.executeBackupDownload(url, error)
      } finally {
        alternate ? this.loadingAltDownload = false : this.loadingDownload = false
      }
    },
    async executeDownload (url, filename, animateProgress) {
      // Fetch the asset
      const response = await nativeFetch(url, { mode: 'cors', })

      // Get the content length to track download progress
      const contentLength = response.headers.get('Content-Length')
      if (!response.body || !contentLength) {
        throw new Error('Readable steams or content length not supported')
      }

      this.retrievingDownloadData = true

      // Create a readable stream
      const totalBytes = parseInt(contentLength, 10)
      let loadedBytes = 0
      const reader = response.body.getReader()
      const stream = new ReadableStream({
        start: async (controller) => {
          while (true) {
            const { done, value } = await reader.read()
            if (done) break

            loadedBytes += value.byteLength
            animateProgress(loadedBytes / totalBytes)

            controller.enqueue(value)
          }
          controller.close()
        }
      })

      const blob = await new Response(stream).blob()
      this.retrievingDownloadData = false

      // Create a downloadable URL for the fetched asset
      const downloadUrl = URL.createObjectURL(blob)
      const fileExtension = url.split('.').pop()
      const fullFilename = `${filename}.${fileExtension}`
      this.triggerDownload(downloadUrl, fullFilename)
    },
    async executeCollectionDownload (assets, filename, animateProgress) {
      // Assets are expected to be an object array with image or video keys

      // Prepare the jszip instance and progress variables
      const zip = new JSZip()
      let totalBytes = 0
      let loadedBytes = 0

      // First we fetch the content length for each asset to track download progress
      const assetSizes = await Promise.all(
        assets.map(async (asset) => {
          const url = asset.image || asset.video
          const response = await nativeFetch(url, { method: 'HEAD', mode: 'cors' })
          return parseInt(response.headers.get('Content-Length'), 10)
        })
      )
      totalBytes = assetSizes.reduce((acc, size) => acc + size, 0)

      this.retrievingDownloadData = true

      // Fetch and add each asset to the zip file
      await Promise.all(
        assets.map(async (asset, index) => {
          const url = asset.image || asset.video
          const response = await nativeFetch(url, { mode: 'cors' })
          if (!response.body) {
            throw new Error('Readable steams not supported')
          }

          const reader = response.body.getReader()
          const fileExtension = url.split('.').pop()
          const fullFilename = `${index}-${filename}.${fileExtension}`
          const fileData = []

          // Create a readable stream
          const stream = new ReadableStream({
            async start(controller) {
              while (true) {
                const { done, value } = await reader.read()
                if (done) break

                fileData.push(value)
                loadedBytes += value.byteLength
                animateProgress(loadedBytes / totalBytes)
                controller.enqueue(value)
              }
              controller.close()
            }
          })

          const blob = await new Response(stream).blob()
          const arrayBuffer = await blob.arrayBuffer()
          zip.file(fullFilename, arrayBuffer)
        })
      )
      this.retrievingDownloadData = false

      // Generate the zip file and download it
      const zipBlob = await zip.generateAsync({ type: 'blob' })
      const zipUrl = URL.createObjectURL(zipBlob)
      this.triggerDownload(zipUrl, `${filename}.zip`)
    },
    async executeBackupDownload (url, error) {
      console.log('Attempting backup download')

      if (!url) {
        console.error('Download failed:', error)
        this.$showAlert({
          message: 'Failed to download asset',
          type: 'error'
        })
        return
      }

      // Open the download url in a new tab
      window.open(url, '_blank')
    },
    triggerDownload (url, downloadFilename) {
      // Create a hidden anchor element to trigger the download
      const anchor = document.createElement('a')
      anchor.href = url
      anchor.download = downloadFilename
      anchor.addEventListener('click', (event) => { event.stopPropagation() })
      document.body.appendChild(anchor)
      anchor.click()
      document.body.removeChild(anchor)

      // Revoke the URL to free up memory
      URL.revokeObjectURL(url)
    },

    // ================================================================================
    // ================================== UI METHODS ==================================
    // ================================================================================

    showIntercom () {
      window.Intercom('show')
    },
    determineAdCardOverflow () {
      if (this.mobileView) {
        this.enableAdCardScroll = true
        return
      }
      this.$nextTick(() => {
        requestAnimationFrame(() => {
          const cardArea = this.$refs["advertisement-card-area"]
          const card = this.$refs["advertisement-card"].$el
          if (cardArea && card) {
            // Subtract 48px for padding and 24px for vert center offset
            const scrollThreshold = cardArea.getBoundingClientRect().height - 48 - 24 
            const cardHeight = card.getBoundingClientRect().height
            this.enableAdCardScroll = cardHeight > scrollThreshold
          }
        })
      })
    },
    refreshBoards () {
      this.selectedBoards = this.getBoards.filter((board) =>
        this.currentAd.board_ids?.includes(board.id)
      )
    },
    getDownloadLabel () {
      if (!this.currentAd.type) return 'Download'
      switch (this.currentAd.type) {
        case 'carousel': return 'Current Card'
        case 'dco': return 'Current Variant'
        default: return this.currentAd.type
      }
    },
    getAltDownloadLabel () {
      if (!this.currentAd.type) return 'Alt'
      switch (this.currentAd.type) {
        case 'video': return 'Thumbnail'
        default: return 'Collection'
      }
    },

    // ================================================================================
    // ================================ EVENT HANDLERS ================================
    // ================================================================================

    handleBreakpointChange (event) {
      this.mobileView = event.matches
      this.$nextTick(() => { this.determineAdCardOverflow() })
    },
    // Handle Ad Share Link Copy
    handleClipboardCopy () {
      this.$showAlert({
        message: 'Copied share link to clipboard!',
        type: 'success'
      })
    },
    handleEmbedClipboardCopy () {
      this.$showAlert({
        message: 'Copied embed code to clipboard!',
        type: 'success'
      })
    },
    playAdVideoAtTimestamp (time) {
      const adCard = this.$refs['advertisement-card']
      if (!adCard) return
      adCard.playVideoAtTimestamp(time)
    },
    onVideoMetadataLoaded () {
      const video = this.$refs.video
      const { duration, videoWidth, videoHeight } = video
      this.assetMetadata = { duration, width: videoWidth, height: videoHeight }
      video.pause()
      video.src = ''
      video.load() // Reinitializing load without source will stop any ongoing download
    },
    handleBoardsDropdownOpened (isOpen) {
      this.SET_PREVENT_DRAWER_ESCAPE_KEY(isOpen)
    },
    formatRunningDuration (start, end) {
      let endTime = end

      if (this.advertisement.live) {
        endTime = new Date().getTime()
      }

      if (isNaN(endTime)) {
        endTime = new Date().getTime()
      }
      // eslint-disable-next-line new-cap
      const startDate = new moment(start)
      // eslint-disable-next-line new-cap
      const endDate = new moment(endTime)

      return Math.ceil(moment.duration(endDate.diff(startDate)).as('days'))
    },
    // Handle Delete Advertisement
    async handleDeleteAdvertisement () {
      this.loadingDelete = true

      try {
        const deleteResponse = await FirebaseAPI.Advertisements.deleteAll([this.currentAd.id])

        if (deleteResponse?.newStorageUsed) {
          this.SET_USER({ ...this.getUser, storageUsed: deleteResponse.newStorageUsed })
        }

        // local update
        this.$emit('removeAds', deleteResponse?.deletedAdIds)

        this.$showAlert({
          message: 'Ad deleted successfully!',
          type: 'success'
        })

        this.$emit('close')
        this.$emit('delete', this.currentAd?.id)
      } catch (e) {
        console.log(e)
        this.$showAlert({
          message: e,
          type: 'error'
        })
      } finally {
        this.loadingDelete = false
      }
    },
    forwardTagEdits (tagId, updatedTag) {
      this.$emit('update:tableViewTags', tagId, updatedTag)
    }
  }
}
</script>

<style scoped>
@property --share-btn-opacity-1 {
  syntax: '<number>';
  initial-value: 0.12;
  inherits: false;
}
@property --share-btn-opacity-2 {
  syntax: '<number>';
  initial-value: 0.12;
  inherits: false;
}

.toolbar {
  container-type: inline-size;
}
@container (width < 732px) {
  .cycle-btn {
    opacity: 0;
    pointer-events: none;
  }
}

.info-card {
  box-shadow: 0px 3px 3px -1.5px rgba(6, 7, 16, 0.04), 0px 1.5px 1.5px -0.75px rgba(6, 7, 16, 0.06), 0px 0px 0.25px 0.75px rgba(6, 7, 16, 0.06);
}
.actions-shadow {
  box-shadow: 0px 1px 2px 0px rgba(4, 26, 75, 0.09), 0px 0px 0px 1px rgba(0, 56, 108, 0.04);
}
.center-toolbar-actions {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
}
.center-toolbar-actions .action-btn:disabled {
  cursor: default;
  background-color: #F6F8FA;
}
.action-btn.enabled {
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: linear-gradient(180deg, rgba(255, 255, 255, var(--share-btn-opacity-1)) 0%, rgba(255, 255, 255, var(--share-btn-opacity-2)) 100%), #006BFF;
  box-shadow: 0px -1px 12px 0px rgba(255, 255, 255, 0.12) inset, 0px 0px 0px 1px #0063F4;
  transition: --share-btn-opacity-1 150ms ease-in-out, --share-btn-opacity-2 150ms ease-in-out;
  transform: scale(0.95);
  transform-origin: center;
}
.action-btn.enabled:hover {
  --share-btn-opacity-1: 0.24;
  --share-btn-opacity-2: 0;
}
.action-btn.enabled:active {
  --share-btn-opacity-1: 0;
  --share-btn-opacity-2: 0.24;
}
.centered-card-wrapper {
  position: absolute;
  top: calc(50% - 20px);
  left: 50%;
  transform: translate(-50%, -50%);
}
.details-organization-grid {
  display: grid;
  grid-template-columns: 136px auto;
  max-width: 100%;
}

.gray-background{
  background-image: url('../../assets/images/gray-striped-background.svg');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
}

.icon-shadow {
  box-shadow: 0px 1px 2px rgba(9, 25, 72, 0.13), 0px 0px 0px 1px rgba(18, 55, 105, 0.08);
  border-radius: 999px;
}

.scrollable-section {
  overflow-y: auto;
}
.scrollable-section::-webkit-scrollbar {
  width: 3px;
}
.scrollable-section::-webkit-scrollbar-thumb {
  background-color: #DFE1E7;
  border-radius: 10000px;
}

.same-creative-link {
  width: 98%;
  transition: width 200ms ease;
}
.same-creative-link:hover {
  width: 100%;
}

.download-dropdown {
  position: absolute;
  bottom: calc(100% + 8px);
  left: 0;
  border-radius: 10px;
  box-shadow: 0px 1px 2px 0px rgba(4, 26, 75, 0.13), 0px 0px 0px 1px rgba(0, 56, 108, 0.08);
}

.gpt-ad-btn {
  box-shadow: 0px 1px 2px 0px rgba(4, 26, 75, 0.13),
    0px 0px 0px 1px rgba(0, 56, 108, 0.08);
  transition: box-shadow 100ms ease-in-out, background-color 100ms ease-in-out;
  border-radius: 6px;
  padding: 6px 8px 6px 8px;
}
.gpt-ad-btn:hover {
  box-shadow: 0px 1px 2px 0px rgba(4, 26, 75, 0.17),
    0px 0px 0px 1px rgba(0, 56, 108, 0.12);
}

.v-enter-active, .v-leave-active {
  transition: opacity 100ms ease-in-out;
}
.v-enter-from, .v-enter, .v-leave-to {
  opacity: 0;
}
.v-enter-to, .v-leave-from {
  opacity: 1;
}
</style>
